import {Box, Grid, type AnimateProps, type GridColumnIndex, type ImageAspectRatio} from '@primer/react-brand'
import {clsx} from 'clsx'

import type {PrimerCards} from '../../../schemas/contentful/contentTypes/primerCards'
import {ContentfulCard} from '../ContentfulCard/ContentfulCard'
import styles from './ContentfulCards.module.css'

/**
 * Uses TypeScript discriminated union to ensure the component accepts either a `component`
 * or a `cards` prop, but not both. This increases the flexibility
 * of this component by supporting either a primerCards entry or a
 * plain array of primerComponentCard entries.
 */
export type ContentfulCardsProps = {
  className?: string
  hasBorder?: boolean
  spanOpts?: {[key: string]: GridColumnIndex}
  fullWidth?: boolean
  animate?: AnimateProps
  imageAspectRatio?: ImageAspectRatio
} & (
  | {
      cards?: never
      component: PrimerCards
    }
  | {
      cards: PrimerCards['fields']['cards']
      component?: never
    }
)

export function ContentfulCards({
  cards,
  component,
  hasBorder,
  fullWidth = false,
  animate,
  imageAspectRatio,
  className,
  spanOpts,
}: ContentfulCardsProps) {
  const collection = component !== undefined ? component.fields.cards : cards

  const desktop = collection.length > 2 ? 4 : 6

  const span: {[key: string]: GridColumnIndex} = {
    xsmall: 12,
    small: 12,
    medium: 6,
    large: desktop,
    xlarge: desktop,
    xxlarge: desktop,
    ...spanOpts,
  }

  const hasMinimalCard = collection.some(item => item?.fields?.variant === 'minimal')

  return (
    <Grid className={clsx(className, hasMinimalCard && styles.withMinimalCard)}>
      {collection.map(card => (
        <Grid.Column span={span} key={card.sys.id}>
          <Box animate={animate} className="height-full">
            <ContentfulCard
              component={card}
              hasBorder={typeof card.fields.hasBorder !== 'undefined' ? card.fields.hasBorder : hasBorder}
              fullWidth={fullWidth}
              imageAspectRatio={imageAspectRatio}
            />
          </Box>
        </Grid.Column>
      ))}
    </Grid>
  )
}

try{ ContentfulCards.displayName ||= 'ContentfulCards' } catch {}